<template lang="pug">
//- The landing page. The copy on this page is also written out as static HTML
//- for crawlers and link previews (homeBodyHtml in build/seo-plugins.ts). When
//- the copy here changes, change it there too, and keep docs/TRACKING.md in
//- step with the data-track names.
//-
//- The page speaks to developers who build with AI coding agents: the base
//- prompt file, the agent's three steps, the codebase route, the MCP server,
//- then the regions, the plans and the news. Same grid, tokens and drawings as
//- the 2026-10-02 page; the headline and the sub line are constants in the
//- script (HEADLINE, HEADLINE_SUB).
.bq-landing
    main
        //- ─── hero: the pitch on the left, the backend drawn on the right ───
        section#what.bq-hero
            .bq-wrap.bq-hero-grid
                .bq-hero-copy
                    //- Text art in place of a logo image: it idles through its
                    //- motions. The art and every motion live in the component.
                    AsciiBanner(:reveal="false")

                    //- The page's one h1, visible this time: it is the headline.
                    h1.bq-hero-title
                        template(v-for="(part, i) in HEADLINE" :key="part")
                            | {{ i ? ' ' : '' }}
                            span.wordset {{ part }}
                    p.bq-hero-sub {{ HEADLINE_SUB }}

                    .bq-hero-actions
                        router-link.bq-btn(
                            data-track="landing_hero_try",
                            :to="user?.user_id ? '/my-projects' : '/signup'"
                        ) Try Skapi &rarr;
                        a.bq-cta-link(href="https://docs.skapi.com" target="_blank" rel="noopener" data-track="landing_docs" data-track-id="hero")
                            | Read the documentation
                            span.bq-arrow.bq-arrow--trail(translate="no") &rarr;

                    //- //- The opening prompt the docs and the Settings page teach,
                    //- //- in place of the install line: it is what a visitor with
                    //- //- an agent does first. Two lines: a br, since Vue collapses
                    //- //- a newline in text to a space.
                    //- .bq-code-option.bq-hero-install
                    //-     span.bq-code-caption prompt
                    //-     CopyText.bq-code(:value="promptText")
                    //-         code
                    //-             | My Skapi project ID is: #[span.tok-str "&lt;Project ID&gt;"].
                    //-             br
                    //-             | Build me a #[span.tok-p [describe what you want]].
                    //-         span.material-symbols-outlined.bq-code-copy(translate="no" aria-hidden="true") content_copy

                //- A stack of four server units on a grid floor, each unit
                //- wired to two of the eight services, and above it the agent's
                //- terminal, where short prompts are typed one after another and
                //- land on the stack. Decorative: the tile words are the feature
                //- grid's, which the page has below, and the prompts are demo
                //- filler (PROMPTS in the script), not site copy.
                .bq-hero-art(aria-hidden="true")
                    //- On a phone the drawing is about half size, so the terminal
                    //- is scaled up two times around its bottom edge and the
                    //- viewBox gains the 80 units of headroom it grows into.
                    svg.bq-art(:viewBox="phone ? '0 -80 640 550' : '0 0 640 470'" fill="none")
                        defs
                            radialGradient#bq-floor-grad(cx="320" cy="300" r="300" gradientUnits="userSpaceOnUse")
                                stop(offset="0.25" stop-color="#fff")
                                stop(offset="1" stop-color="#fff" stop-opacity="0")
                            mask#bq-floor-fade(maskUnits="userSpaceOnUse" x="0" y="0" width="640" height="470")
                                rect(x="0" y="0" width="640" height="470" fill="url(#bq-floor-grad)")
                        //- the floor
                        g.bq-art-floor(mask="url(#bq-floor-fade)")
                            line(v-for="(l, i) in FLOOR" :key="i" :x1="l[0]" :y1="l[1]" :x2="l[2]" :y2="l[3]")
                        //- the wires, each with a second stroke of moving dashes
                        g(v-for="w in WIRES" :key="w.d")
                            path.bq-art-wire(:d="w.d")
                            path.bq-art-flow(:d="w.d" :class="{ 'bq-art-flow--out': w.out }")
                        //- the stack, bottom unit first so each one covers the one below
                        g.bq-art-slab(v-for="(s, i) in SLABS" :key="i" :class="{ 'bq-art-slab--top': s.isTop }")
                            polygon.bq-art-face-top(:points="s.top")
                            polygon.bq-art-face-left(:points="s.left")
                            polygon.bq-art-face-right(:points="s.right")
                            polygon.bq-art-led(v-for="(p, j) in s.leds" :key="j" :points="p")
                            line.bq-art-vent(v-for="(v, j) in s.vents" :key="'v' + j" :x1="v[0]" :y1="v[1]" :x2="v[2]" :y2="v[3]")
                            rect.bq-art-port(:x="s.portL[0] - 3" :y="s.portL[1] - 3" width="6" height="6")
                            rect.bq-art-port(:x="s.portR[0] - 3" :y="s.portR[1] - 3" width="6" height="6")
                        //- the landing pad on the top unit, and the agent's
                        //- terminal above it: the prompt comes down the beam
                        polygon.bq-art-pad(:points="PAD")
                        path.bq-art-beam(d="M320 68V124")
                        path.bq-art-flow(d="M320 68V124")
                        //- placed by the outer group; the middle one is scaled
                        //- up on a phone; the inner one bobs (a CSS transform
                        //- would replace the placement attribute, so each has its own)
                        g(transform="translate(225 6)")
                          g.bq-art-term-scale
                            g.bq-art-term.bq-art-term--bob
                                rect.bq-art-term-box(x="0" y="0" width="190" height="62" rx="3")
                                line.bq-art-term-bar(x1="0" y1="16" x2="190" y2="16")
                                circle.bq-art-term-dot(v-for="x in [10, 20, 30]" :key="x" :cx="x" cy="8" r="2.2")
                                text.bq-art-term-text(x="10" y="36") &gt; {{ typed[0] }}#[tspan.bq-art-cursor(v-if="cursorLine === 0" translate="no") &#9615;]
                                text.bq-art-term-text(x="10" y="52" v-if="cursorLine === 1") &gt; {{ typed[1] }}#[tspan.bq-art-cursor(v-if="cursorLine === 1" translate="no") &#9615;]
                        //- the service tiles
                        g.bq-art-tile(v-for="t in TILES" :key="t.label[0]")
                            rect.bq-art-tile-shadow(:x="t.x - 25" :y="t.y - 25" width="56" height="56")
                            rect.bq-art-tile-box(:x="t.x - 28" :y="t.y - 28" width="56" height="56")
                            Glyph(as="g" :shapes="t.icon" :transform="`translate(${t.x - 12} ${t.y - 12})`")
                            text.bq-art-label(:x="t.x" :y="t.y + 47")
                                tspan(v-for="(line, i) in t.label" :key="line" :x="t.x" :dy="i ? 16 : 0") {{ line }}

        //- ─── what the agent gets: a ruled grid ───
        section#features.bq-section
            .bq-wrap
                .bq-section-header
                    .bq-section-heading
                        span.bq-pill WHAT IS SKAPI
                        h2 Your #[span.wordset Entire Backend,] #[span.wordset Out of the Box]
                    p.bq-section-sub Everything below ships with your Skapi project, ready the moment it is created.
                ul.bq-feature-grid
                    li.bq-feature(v-for="f in FEATURES" :key="f.name")
                        Glyph.bq-icon(:shapes="f.icon")
                        span.bq-feature-name {{ f.name }}
                        span.bq-feature-desc {{ f.desc }}

        //- ─── codebase: the same library straight from the project's code, NodeJS or a plain page ───
        section#codebase.bq-section.bq-section--soft.bq-section-top(style='padding-bottom:0')
            .bq-wrap
                .bq-section-header
                    .bq-section-heading
                        span.bq-pill HOW IT WORKS
                        //- span.bq-pill CODEBASE
                        h2 Setting Up #[span.wordset Your Codebase]
                    p.bq-section-sub A complete backend in one JavaScript library, compatible with any JS framework or plain HTML.
                .bq-how-grid
                    //- Two ways in, not steps: either one is the whole setup. Two
                    //- panels with a rule between them, no rail and no numbers.
                    .bq-ways
                        .bq-way
                            span.bq-step-label Your Skapi project
                            h3 Create a Skapi project
                            p Give it a name, choose a region, and Skapi gives you its Project ID.
                            p.bq-step-link
                                router-link.bq-cta-link(to="/new-project" data-track="landing_step_new_project")
                                    | Create a project
                                    span.bq-arrow.bq-arrow--trail(translate="no") &rarr;
                        .bq-code-or
                            span then
                        .bq-way
                            span.bq-step-label NodeJS and SPA projects
                            h3 Install the library
                            p Install #[span(translate="no") skapi-js] from npm, create the instance once with your Project ID, and import it wherever it is used.
                            .bq-code-option
                                span.bq-code-caption bash
                                CopyText.bq-code(:value="installCmd")
                                    code #[span.tok-kw npm] i skapi-js@{{ npmVersion }}
                                    span.material-symbols-outlined.bq-code-copy(translate="no" aria-hidden="true") content_copy
                            .bq-code-option
                                span.bq-code-caption javascript
                                CopyText.bq-code(:value="nodeConnectCode")
                                    //- Two lines: a br, since Vue collapses a newline in text to a space.
                                    code
                                        | #[span.tok-kw import] { #[span.tok-cls Skapi] } #[span.tok-kw from] #[span.tok-str 'skapi-js'];
                                        br
                                        | #[span.tok-kw const] skapi = #[span.tok-kw new] #[span.tok-cls Skapi](#[span.tok-str "&lt;Project ID&gt;"]);
                                    span.material-symbols-outlined.bq-code-copy(translate="no" aria-hidden="true") content_copy
                        .bq-code-or
                            span or
                        .bq-way
                            span.bq-step-label Plain HTML
                            h3 Load it with a script tag
                            p No build step: load the library from the CDN and initialize it on the page. Your backend is ready to use.
                            .bq-code-option
                                span.bq-code-caption index.html
                                CopyText.bq-code(:value="htmlPageCode")
                                    //- One line per br, since Vue collapses a newline in text to a space; the indent is &nbsp;, which it keeps.
                                    code
                                        | #[span.tok-p &lt;!]#[span.tok-tag DOCTYPE] #[span.tok-attr html]#[span.tok-p &gt;]
                                        br
                                        | #[span.tok-p &lt;]#[span.tok-tag script] #[span.tok-attr src]=#[span.tok-str "https://cdn.jsdelivr.net/npm/skapi-js@{{ npmVersion }}/dist/skapi.js"]#[span.tok-p &gt;&lt;/]#[span.tok-tag script]#[span.tok-p &gt;]
                                        br
                                        | #[span.tok-p &lt;]#[span.tok-tag script]#[span.tok-p &gt;]
                                        br
                                        | &nbsp;&nbsp;&nbsp;&nbsp;#[span.tok-kw const] skapi = #[span.tok-kw new] #[span.tok-cls Skapi](#[span.tok-str "&lt;Project ID&gt;"]);
                                        br
                                        | #[span.tok-p &lt;/]#[span.tok-tag script]#[span.tok-p &gt;]
                                    span.material-symbols-outlined.bq-code-copy(translate="no" aria-hidden="true") content_copy
                    .bq-how-art(aria-hidden="true")
                        svg.bq-art(viewBox="0 0 260 430" fill="none")
                            defs
                                radialGradient#bq-mini-grad(cx="130" cy="350" r="135" gradientUnits="userSpaceOnUse")
                                    stop(offset="0.2" stop-color="#fff")
                                    stop(offset="1" stop-color="#fff" stop-opacity="0")
                                mask#bq-mini-fade(maskUnits="userSpaceOnUse" x="0" y="0" width="260" height="430")
                                    rect(x="0" y="0" width="260" height="430" fill="url(#bq-mini-grad)")
                            g.bq-art-floor(mask="url(#bq-mini-fade)")
                                line(v-for="(l, i) in MINI_FLOOR" :key="i" :x1="l[0]" :y1="l[1]" :x2="l[2]" :y2="l[3]")
                            //- the page
                            rect.ill-surface(x="26" y="16" width="208" height="108")
                            line.ill-ink(x1="26" y1="36" x2="234" y2="36")
                            path.ill-ink(d="M37 26h.01M45 26h.01")
                            line.ill-accent(x1="44" y1="56" x2="112" y2="56")
                            line.ill-line(x1="58" y1="72" x2="186" y2="72")
                            line.ill-line(x1="58" y1="88" x2="158" y2="88")
                            line.ill-accent(x1="44" y1="104" x2="88" y2="104")
                            //- the Skapi mark between the page and the backend, on the wire
                            image.bq-art-logo(href="/skapi-logo-cut-small.png" x="107" y="160" width="46" height="57")
                            //- the backend
                            g.bq-art-slab(v-for="(s, i) in MINI_SLABS" :key="i" :class="{ 'bq-art-slab--top': s.isTop }")
                                polygon.bq-art-face-top(:points="s.top")
                                polygon.bq-art-face-left(:points="s.left")
                                polygon.bq-art-face-right(:points="s.right")
                                polygon.bq-art-led(v-for="(p, j) in s.leds" :key="j" :points="p")
                                line.bq-art-vent(v-for="(v, j) in s.vents" :key="'v' + j" :x1="v[0]" :y1="v[1]" :x2="v[2]" :y2="v[3]")
                            polygon.bq-art-pad(:points="MINI_PAD")
                            //- one wire through all three
                            path.bq-art-wire(d="M130 124V156M130 221V296")
                            path.bq-art-flow(d="M130 124V156M130 221V296")
                            rect.bq-art-port(x="127" y="121" width="6" height="6")
                            rect.bq-art-port(x="127" y="293" width="6" height="6")
                p.bq-section-link
                    a.bq-cta-link(href="https://docs.skapi.com/introduction/getting-started.html" target="_blank" rel="noopener" data-track="landing_docs" data-track-id="codebase")
                        | Read the documentation
                        span.bq-arrow.bq-arrow--trail(translate="no") &rarr;

        //- ─── how it works: a project, the base prompt, the first prompt ───
        section#how.bq-section.bq-section--soft
            .bq-wrap
                .bq-section-header
                    .bq-section-heading
                        span.bq-pill AI CODING
                        h2 Build With Your #[span.wordset AI Agent]
                    p.bq-section-sub Code with your favorite CLI agent, IDE or #[span.wordset AI platform.]
                ol.bq-steps.bq-steps--wide
                    li.bq-step
                        span.bq-step-node
                            Glyph.bq-icon(:shapes="ICON.download")
                        .bq-step-body
                            span.bq-step-label Step 1
                            h3 Download your tool's base prompt file
                            p Choose your tool below and click its file name to download the base prompt.
                            //- The same download the Settings page offers (code/agent-prompts.ts):
                            //- one file from the CDN, saved under the name the tool reads.
                            ul.bq-prompt-list
                                li.bq-prompt-row(v-for="p in AGENT_PLATFORMS" :key="p.key")
                                    svg.bq-prompt-logo(viewBox="0 0 24 24" aria-hidden="true" focusable="false")
                                        path(:d="AGENT_LOGOS[p.key]")
                                    span.bq-prompt-tool {{ p.name }}
                                    //- the file name is the link: what the click saves
                                    a.bq-cta-link.bq-prompt-get(
                                        :href="AGENT_PROMPT_URL"
                                        :download="p.filename"
                                        data-track="landing_prompt_download"
                                        :data-track-id="p.key"
                                        :class="{ disabled: downloading }"
                                        translate="no"
                                        @click.prevent="download(p)"
                                    )
                                        | {{ downloading === p.filename ? "Saving..." : p.filename }}
                    li.bq-step
                        span.bq-step-node
                            Glyph.bq-icon(:shapes="ICON.files")
                        .bq-step-body
                            span.bq-step-label Step 2
                            h3 Put the file in your project folder
                            p Save the base prompt file in your project's root folder, or attach the file directly to your agent's prompt input.
                    li.bq-step
                        span.bq-step-node
                            Glyph.bq-icon(:shapes="ICON.terminal")
                        .bq-step-body
                            span.bq-step-label Step 3
                            h3 Open your agent and start building
                            p Paste the prompt below into your agent and describe what you want to build. Replace &lt;Project ID&gt; with your actual Skapi project ID.
                            .bq-code-option
                                span.bq-code-caption prompt
                                CopyText.bq-code(:value="promptText")
                                    code
                                        | My Skapi project ID is: #[span.tok-str "&lt;Project ID&gt;"].
                                        br
                                        | Build me a #[span.tok-p [describe what you want]].
                                    span.material-symbols-outlined.bq-code-copy(translate="no" aria-hidden="true") content_copy
                p.bq-section-link
                    a.bq-cta-link(href="https://docs.skapi.com/introduction/getting-started.html#building-with-your-ai-agent" target="_blank" rel="noopener" data-track="landing_docs" data-track-id="how")
                        | Read the documentation
                        span.bq-arrow.bq-arrow--trail(translate="no") &rarr;


        
        //- ─── why: four reasons, each with its drawing ───
        section#why.bq-section.bq-section-top(style='padding-bottom:0')
            .bq-wrap
                .bq-section-header
                    .bq-section-heading
                        span.bq-pill WHY SKAPI
                        h2 #[span.wordset Lower Bills,] #[span.wordset Sealed Data,] #[span.wordset 100% Serverless]
                    p.bq-section-sub Pay a fraction of the cost, keep your keys and user data sealed, and run with nothing to manage.
                .bq-why-grid
                    .bq-why
                        //- Ten blocks beside one: one-tenth.
                        .bq-why-art(aria-hidden="true")
                            svg(viewBox="0 0 280 150" fill="none")
                                line.ill-line(x1="40" y1="132" x2="240" y2="132")
                                rect.ill-block(v-for="n in 10" :key="n" x="64" :y="131.5 - n * 11.5" width="58" height="9")
                                rect.ill-block.ill-block--accent(x="162" y="120" width="58" height="9")
                                path.ill-accent.ill-dash(d="M130 20.5H191V104")
                                path.ill-accent(d="M184 98l7 9 7-9")
                        .bq-why-body
                            h3 Cut Backend Costs #[span.wordset by Up to 90%]
                            p No servers to rent, no idle capacity, no one on call. Skapi charges only for what your app actually uses, so a backend that costs hundreds a month on cloud servers can run for as little as a tenth of that. And the Free plan covers your start.
                    .bq-why
                        //- Two loops: six steps circling back on themselves,
                        //- and one step with the shortest loop there is.
                        .bq-why-art(aria-hidden="true")
                            svg(viewBox="0 0 280 150" fill="none")
                                rect.ill-block(v-for="n in 6" :key="n" :x="20 + (n - 1) * 42" y="30" width="30" height="18")
                                path.ill-line(v-for="n in 5" :key="'a' + n" :d="`M${50 + (n - 1) * 42} 39h12`")
                                path.ill-accent.ill-dash(d="M245 48v22H35v-14")
                                path.ill-accent(d="M29 62l6-7 6 7")
                                rect.ill-block.ill-block--accent(x="20" y="100" width="30" height="18")
                                path.ill-accent.ill-dash(d="M35 118v12h32v-21h-10")
                                path.ill-accent(d="M62 103l-6 6 6 6")
                        .bq-why-body
                            h3 Fewer Tokens #[span.wordset to Build]
                            p On a conventional backend, every schema change sends your agent through a long loop: list tables, write a migration, apply it, write policies, regenerate types, fix what breaks, then edit the app. On Skapi, there's one step: edit the app. Your data has no schema to rebuild, ever.
                    .bq-why
                        //- A browser window with the lock inside it, and what
                        //- leaves the window already sealed.
                        .bq-why-art(aria-hidden="true")
                            svg(viewBox="0 0 280 150" fill="none")
                                rect.ill-surface(x="24" y="22" width="134" height="108")
                                line.ill-ink(x1="24" y1="40" x2="158" y2="40")
                                path.ill-ink(d="M33 31h.01M41 31h.01")
                                line.ill-line(x1="38" y1="54" x2="104" y2="54")
                                line.ill-line(x1="38" y1="64" x2="132" y2="64")
                                rect.ill-block.ill-block--accent(x="77" y="92" width="28" height="22")
                                path.ill-accent(d="M83 92v-7a8 8 0 0 1 16 0v7")
                                path.ill-accent.ill-flow(d="M158 103H262")
                                rect.ill-cipher(x="174" y="96" width="14" height="14")
                                rect.ill-cipher.ill-cipher--accent(x="198" y="96" width="14" height="14")
                                rect.ill-cipher(x="222" y="96" width="14" height="14")
                                rect.ill-cipher.ill-cipher--accent(x="246" y="96" width="14" height="14")
                        .bq-why-body
                            h3 Your Data. Encrypted #[span.wordset Before It Leaves the Browser]
                            p Your users' data is sealed with AES-256-GCM on their own device even before it ever touches the network. Not even Skapi can read it.
                    .bq-why
                        //- A cloud fanning out to as many nodes as are needed,
                        //- the outer ones still arriving.
                        .bq-why-art(aria-hidden="true")
                            svg(viewBox="0 0 280 150" fill="none")
                                path.ill-line(v-for="x in NODE_X" :key="'l' + x" :d="`M140 82L${x} 116`")
                                g(transform="translate(92 6) scale(4)")
                                    path.ill-surface.ill-scaled(d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z")
                                    path.ill-accent.ill-scaled(d="M9 14l2 2 4-4")
                                rect.ill-node(v-for="(x, i) in NODE_X" :key="x" :x="x - 6" y="116" width="12" height="12" :class="{ 'ill-node--ghost': i < 2 || i > 4 }")
                        .bq-why-body
                            h3 Serverless by Design, #[span.wordset Nothing to Deploy]
                            p No servers, no deployments, no capacity limits. Your backend is live the moment you create the project, scales without limit, and is built to stay up, so there's nothing for you to maintain.
        
        //- ─── the MCP server: optional, the administrative side; three steps
        //- on the rail. The snippets and notes mirror the docs' "Connecting the
        //- Skapi MCP server" (introduction/getting-started.md); change both together ───
        section#mcp.bq-section
            .bq-wrap
                .bq-section-header
                    .bq-section-heading
                        span.bq-pill MCP SERVER
                        //- span.bq-pill MCP SERVER
                        //- h2 Let Your Agent #[span.wordset Run the Project]
                        h2 Connecting the #[span.wordset MCP Server]
                    p.bq-section-sub You can let your agent manage your Skapi projects, from your coding tool or any MCP-compatible chat platform.
                p.bq-section-intro Tickets, secret keys, OpenID sign-in, settings, hosting, records, email templates, users: all the complex dashboard settings, done with a prompt instead.
                .bq-how-grid
                    ol.bq-steps
                        li.bq-step
                            span.bq-step-node
                                Glyph.bq-icon(:shapes="ICON.link")
                            .bq-step-body
                                span.bq-step-label Step 1
                                h3 Connect from your tool
                                p Pick your tool and add the server with its line.
                                //- One tab per tool above phone width, a select on a
                                //- phone; both pick the same panel. Arrow keys move
                                //- between tabs.
                                .bq-tabs(role="tablist" aria-label="Coding agents")
                                    button.bq-tab(
                                        v-for="m in MCP_SETUPS"
                                        :key="m.key"
                                        type="button"
                                        role="tab"
                                        :id="`mcp-tab-${m.key}`"
                                        :aria-selected="mcpTab === m.key ? 'true' : 'false'"
                                        :aria-controls="`mcp-panel-${m.key}`"
                                        :tabindex="mcpTab === m.key ? 0 : -1"
                                        :class="{ active: mcpTab === m.key }"
                                        @click="mcpTab = m.key"
                                        @keydown.left.prevent="stepTab(-1)"
                                        @keydown.right.prevent="stepTab(1)"
                                    )
                                        svg.bq-tab-logo(viewBox="0 0 24 24" aria-hidden="true" focusable="false")
                                            path(:d="AGENT_LOGOS[m.key]")
                                        span {{ m.name }}
                                //- A native select can hold no icons, so the box shows
                                //- the chosen tool's logo and the site's dropdown arrow
                                //- over it; the select underneath does the picking.
                                label.bq-tab-select
                                    span.bq-sr-only Coding agent
                                    svg.bq-tab-select-logo(viewBox="0 0 24 24" aria-hidden="true" focusable="false")
                                        path(:d="AGENT_LOGOS[mcpTab]")
                                    select(v-model="mcpTab")
                                        option(v-for="m in MCP_SETUPS" :key="m.key" :value="m.key") {{ m.name }}
                                    span.material-symbols-outlined.bq-tab-select-arrow(translate="no" aria-hidden="true") arrow_drop_down
                                .bq-mcp-panel(
                                    v-for="m in MCP_SETUPS"
                                    v-show="mcpTab === m.key"
                                    :key="'panel-' + m.key"
                                    role="tabpanel"
                                    :id="`mcp-panel-${m.key}`"
                                    :aria-labelledby="`mcp-tab-${m.key}`"
                                )
                                    .bq-code-option
                                        span.bq-code-caption {{ m.lang }}
                                        CopyText.bq-code(:value="m.code")
                                            code(translate="no") {{ m.code }}
                                            span.material-symbols-outlined.bq-code-copy(translate="no" aria-hidden="true") content_copy
                                    p.bq-mcp-where {{ m.where }}
                        li.bq-step
                            span.bq-step-node
                                Glyph.bq-icon(:shapes="ICON.auth")
                            .bq-step-body
                                span.bq-step-label Step 2
                                h3 Sign in with your Skapi account
                                p The first time, your browser opens the Skapi sign-in page. Standard OAuth, nothing to configure. From then on the agent acts as your account and can reach only what you can.
                        li.bq-step
                            span.bq-step-node
                                Glyph.bq-icon(:shapes="ICON.create")
                            .bq-step-body
                                span.bq-step-label Step 3
                                h3 Tell it which project
                                p An account can hold several Skapi projects. Say which one, or ask the agent to list them. Then ask for what you need.
                                .bq-code-option
                                    span.bq-code-caption prompt
                                    CopyText.bq-code(:value="mcpProjectPrompt")
                                        code Use my Skapi project #[span.tok-str "&lt;Project ID&gt;"].
                                        span.material-symbols-outlined.bq-code-copy(translate="no" aria-hidden="true") content_copy
                    //- The drawing: the agent's window, the MCP server signed in as the
                    //- account, the project's mark, and the dashboard forms the server
                    //- fills in, on one wire. Hidden with the grid's art column on narrow screens.
                    .bq-how-art(aria-hidden="true")
                        svg.bq-art(viewBox="0 0 260 370" fill="none")
                            //- the agent: a terminal with two prompts
                            g.bq-art-term
                                rect.bq-art-term-box.bq-art-box--light(x="26" y="14" width="208" height="74" rx="3")
                                line.bq-art-term-bar.bq-art-box--light(x1="26" y1="30" x2="234" y2="30")
                                circle.bq-art-term-dot(v-for="x in [36, 46, 56]" :key="x" :cx="x" cy="22" r="2.2")
                                text.bq-art-term-text(x="36" y="52") &gt; {{ mcpTyped[0] }}#[tspan.bq-art-cursor(v-if="mcpCursorLine === 0" translate="no") &#9615;]
                                text.bq-art-term-text(x="36" y="70" v-if="mcpCursorLine === 1") &gt; {{ mcpTyped[1] }}#[tspan.bq-art-cursor(v-if="mcpCursorLine === 1" translate="no") &#9615;]
                            //- the server, signed in as the account
                            rect.bq-art-term-box.bq-art-box--light(x="74" y="118" width="112" height="50" rx="3")
                            text.bq-art-node-text(x="130" y="141") MCP
                            text.bq-art-caption(x="130" y="158") signed in as you
                            //- the project
                            image.bq-art-logo(href="/skapi-logo-cut-small.png" x="107" y="192" width="46" height="57")
                            //- the dashboard forms it fills in
                            g.bq-art-tile(v-for="t in MCP_TILES" :key="t.label")
                                rect.bq-art-tile-shadow(:x="t.x - 19" y="293" width="44" height="44")
                                rect.bq-art-tile-box(:x="t.x - 22" y="290" width="44" height="44")
                                Glyph(as="g" :shapes="t.icon" :transform="`translate(${t.x - 12} 300)`")
                                text.bq-art-caption(:x="t.x" y="352") {{ t.label }}
                            //- one wire down through all of it, fanning out to the forms
                            path.bq-art-wire(d="M130 88V118M130 168V192M130 249V276M64 276H196M64 276V290M130 276V290M196 276V290")
                            path.bq-art-flow(d="M130 88V118M130 168V192M130 249V276")
                            rect.bq-art-port(x="127" y="85" width="6" height="6")
                p.bq-section-link
                    a.bq-cta-link(href="https://docs.skapi.com/introduction/getting-started.html#connecting-the-skapi-mcp-server" target="_blank" rel="noopener" data-track="landing_docs" data-track-id="mcp")
                        | Read the documentation
                        span.bq-arrow.bq-arrow--trail(translate="no") &rarr;

        
        //- ─── regions: the world, dotted, with a pin on every region a project can live in ───
        section#regions.bq-section.bq-section--soft-up
            .bq-wrap
                .bq-section-header
                    .bq-section-heading
                        //- span.bq-pill REGIONS
                        h2 Close to Your Users, #[span.wordset Wherever They Are]
                    p.bq-section-sub Serve your web service from regions around the world and give your users the shortest hop to your backend. More regions are on the way.
                //- The land as dots (code/world-dots.ts); the pins are the regions in
                //- service (LIVE_REGIONS in the script, placed by code/regions.ts).
                //- Labels go under 640px; the list below stays.
                //- On a phone the panel runs edge to edge, borderless, and the drawing,
                //- kept at a readable scale, pans slowly and endlessly: two copies on a
                //- track, the track slid by one copy's width and looped (the Pacific
                //- meets itself at the seam). Above phone width one copy, still.
                .bq-map(aria-hidden="true")
                    .bq-map-track
                        svg.bq-map-svg(v-for="copy in 2" :key="copy" :class="`bq-map-copy--${copy}`" :viewBox="`0 0 ${WORLD_VIEW.width} ${WORLD_VIEW.height}`" fill="none")
                            path.bq-map-dots(:d="WORLD_DOTS")
                            g.bq-map-pin(v-for="r in PINS" :key="r.region" :transform="`translate(${r.x} ${r.y})`")
                                circle.bq-map-ring(r="7")
                                circle.bq-map-spot(r="3")
                                image.bq-map-mark(href="/skapi-logo-cut-small.png" x="-9" y="-33" width="18" height="22")
                                text.bq-map-label(y="20") {{ r.city }}
                ul.bq-map-list
                    li(v-for="r in PINS" :key="r.region") {{ r.label }}

        
        //- ─── pricing ───
        //- The cards are the subscription page's own PlanCards, so a price,
        //- description or limit can never differ between the two. Each card is
        //- one link stretched over the whole card: a signed-in visitor gets the
        //- New Project dialog right here with that plan chosen (Free creates and
        //- opens the project, a paid plan goes to checkout), anyone else goes to
        //- the signup page. //
        section#pricing.bq-section.bq-section--grid.bq-section-top
            .bq-wrap
                .bq-section-header
                    .bq-section-heading
                        span.bq-pill PRICING
                        h2 Start Free, #[span.wordset Scale as You Grow]
                    p.bq-section-sub Every project starts on Free. Upgrade when it needs more.
                PlanCards.bq-pricing-cards(:showFreePlan="true")
                    template(#freeAction)
                        a.bq-plan-link(v-if="user?.user_id" href="/my-projects" @click.prevent="openCreate('free')" data-track="landing_pricing_free")
                            span.bq-btn.bq-plan-cta.bq-plan-cta--ghost Start for Free
                        router-link.bq-plan-link(v-else to="/signup" data-track="landing_pricing_free")
                            span.bq-btn.bq-plan-cta.bq-plan-cta--ghost Start for Free
                    template(#standardAction)
                        a.bq-plan-link(v-if="user?.user_id" href="/my-projects" @click.prevent="openCreate('standard')" data-track="landing_pricing_standard")
                            span.bq-btn.bq-plan-cta.bq-plan-cta--ghost Choose Standard
                        router-link.bq-plan-link(v-else to="/signup" data-track="landing_pricing_standard")
                            span.bq-btn.bq-plan-cta.bq-plan-cta--ghost Choose Standard
                    template(#premiumAction)
                        a.bq-plan-link(v-if="user?.user_id" href="/my-projects" @click.prevent="openCreate('premium')" data-track="landing_pricing_premium")
                            span.bq-btn.bq-plan-cta Choose Premium
                        router-link.bq-plan-link(v-else to="/signup" data-track="landing_pricing_premium")
                            span.bq-btn.bq-plan-cta Choose Premium
                //- The cards list headline figures only; how sends are counted,
                //- what the hard stop means and the rest are in the docs.
                p.bq-section-link
                    a.bq-cta-link(:href="PLAN_DOCS.plans" target="_blank" rel="noopener" data-track="landing_docs" data-track-id="plans")
                        | How plan limits work
                        span.bq-arrow.bq-arrow--trail(translate="no") &rarr;

        //- ─── latest articles; hidden until the list loads, for good if it fails ───
        section#news.bq-section.bq-section-top(v-if="newsArticles.length" style='padding-bottom:0')
            .bq-wrap
                .bq-section-header
                    .bq-section-heading
                        span.bq-pill NEWS &amp; UPDATES
                        h2 Latest from Skapi
                    p.bq-section-sub Release notes, announcements, and stories from the team.
                .bq-card-grid
                    router-link.bq-card(
                        v-for="a in newsArticles"
                        :key="a.slug"
                        :to="`/articles/${a.slug}`"
                        data-track="landing_article"
                    )
                        time.bq-news-date(translate="no") {{ a.date }}
                        h3 {{ a.title }}
                        p.bq-news-excerpt(v-if="a.excerpt") {{ a.excerpt }}
                        span.bq-card-more(aria-hidden="true") Read &rarr;
                p.bq-section-link
                    router-link.bq-cta-link(to="/articles" data-track="landing_articles_all")
                        | All articles
                        span.bq-arrow.bq-arrow--trail(translate="no") &rarr;

        //- ─── closing panel, on the same grid the hero's stack stands on ───
        section.bq-section.bq-close
            .bq-wrap
                .bq-close-panel
                    img(src="/skapi-logo-cut-small.png" alt="Skapi logo" width="244" height="300" loading="lazy")
                    h2 Try Skapi #[span.wordset for Free]
                    p Free to start. #[span.wordset No credit card required.]
                    router-link.bq-btn(
                        data-track="landing_bottom_try",
                        :to="user?.user_id ? '/my-projects' : '/signup'"
                    ) Try Skapi &rarr;

    //- From a plan card, signed in: name the project here, then Free creates and
    //- opens it and a paid plan goes to checkout (views/new-project.ts).
    CreateProject(
        :showCreateModal="showCreate"
        :initialPlan="createPlan"
        @close="showCreate = false"
    )
</template>

<script setup>
import { user } from "@/code/user";
import { npmVersion } from "@/main";
import AsciiBanner from "@/components/asciiBanner.vue";
import CopyText from "@/components/copyText.vue";
import PlanCards from "@/components/planCards.vue";
import CreateProject from "@/views/project-create.vue";
import { PLAN_DOCS } from "@/code/plan-copy";
import { AGENT_PLATFORMS, AGENT_PROMPT_URL, downloadAgentPrompt } from "@/code/agent-prompts";
import { AGENT_LOGOS } from "@/code/agent-logos";
import { catalogRegion } from "@/code/regions";
import { WORLD_DOTS, WORLD_VIEW, project } from "@/code/world-dots";
import { showToast } from "@/components/toast";
import { removeFullScreenLoader } from "@/components/jsComponents";
import { getArticles } from "@/code/articles";
import { computed, h, onBeforeUnmount, onMounted, ref } from "vue";

// A plan card, signed in: the New Project dialog with that plan chosen.
const createPlan = ref("");
const showCreate = ref(false);
const openCreate = (plan) => {
    createPlan.value = plan;
    showCreate.value = true;
};

const newsArticles = ref([]);

// Every code sample reads the one npmVersion ref from main.ts: "latest" until
// the npm registry answers, then the published version. These are the exact
// strings CopyText puts on the clipboard; the template renders the same text.
const installCmd = computed(() => `npm i skapi-js@${npmVersion.value}`);
// The tag name is spliced in so this file never contains a literal script
// tag: one would end the SFC block early, and even the escaped form trips
// editor highlighters.
const TAG = "script";
// The whole page for the plain-HTML route: the CDN tag, pinned to the version
// npmVersion holds like every other sample, then the instance.
const htmlPageCode = computed(() => `<!DOCTYPE html>
<${TAG} src="https://cdn.jsdelivr.net/npm/skapi-js@${npmVersion.value}/dist/skapi.js"></${TAG}>
<${TAG}>
    const skapi = new Skapi("<Project ID>");
</${TAG}>`);
// The module import for NodeJS and SPA projects.
const nodeConnectCode = `import { Skapi } from 'skapi-js';
const skapi = new Skapi("<Replace this with your Project ID>");`;

// ─── the headline ───
// One place to change while the candidates are compared. The parts are the
// groups the line may break between; the sub says who the agents are.
const HEADLINE = ["Instant Backend API", "for AI Agents"];
const HEADLINE_SUB = "Everything your web app needs from a backend. Your agent builds it from one base prompt.";

// The opening prompt, as the docs and the Settings page teach it (the same
// text code/agent-prompts.ts fills the project id into), and the one line
// that connects the MCP server (docs introduction/ai-driven.md).
const promptText = `My Skapi project ID is: "<Project ID>".
Build me a [describe what you want].`;
// The line that names the project once the MCP server is connected (docs, same section).
const mcpProjectPrompt = `Use my Skapi project "<Project ID>".`;

// ─── the MCP server ───
// The docs' per-tool snippets (introduction/getting-started.md, "Coding
// agents"), with the table's "where it goes / signing in" folded into one line
// each. Change both together. The keys are agent-logos.ts's.
const MCP_URL = "https://mcp.broadwayinc.computer";
const MCP_SETUPS = [
    {
        key: "claude",
        name: "Claude Code",
        lang: "bash",
        code: `claude mcp add --transport http skapi ${MCP_URL}`,
        where: "Add --scope user to have it in every project. Run /mcp in a session if it has not asked you to sign in.",
    },
    {
        key: "openai",
        name: "Codex CLI",
        lang: "bash",
        code: `codex mcp add skapi --url ${MCP_URL}\ncodex mcp login skapi`,
        where: "The second line signs you in.",
    },
    {
        key: "gemini",
        name: "Gemini CLI",
        lang: "bash",
        code: `gemini mcp add --transport http skapi ${MCP_URL}`,
        where: "Add -s user to have it in every project.",
    },
    {
        key: "cursor",
        name: "Cursor",
        lang: "json",
        code: `{\n  "mcpServers": {\n    "skapi": { "url": "${MCP_URL}" }\n  }\n}`,
        where: "Goes in .cursor/mcp.json in the project, or ~/.cursor/mcp.json for every project.",
    },
    {
        key: "copilot",
        name: "VS Code",
        lang: "json",
        code: `{\n  "servers": {\n    "skapi": { "type": "http", "url": "${MCP_URL}" }\n  }\n}`,
        where: "Goes in .vscode/mcp.json, or use the Command Palette: MCP: Add Server.",
    },
    {
        key: "windsurf",
        name: "Windsurf",
        lang: "json",
        code: `{\n  "mcpServers": {\n    "skapi": { "serverUrl": "${MCP_URL}" }\n  }\n}`,
        where: "Goes in ~/.config/devin/mcp_config.json (Windsurf is now Devin Desktop).",
    },
];

// ─── the map's pins ───
// The regions in service, the same on every environment: production's VITE_REG
// list (.env.production), so the dev site shows the real map and not its own
// two test stacks. Add a code here when a region launches. The catalogue
// (code/regions.ts) places and names each one; the label on the map is the
// city alone, the list under it the full name.
const LIVE_REGIONS = ["ap-northeast-2", "us-west-2", "ap-southeast-1", "ap-south-1", "eu-west-3", "sa-east-1"];
const PINS = LIVE_REGIONS
    .map((code) => catalogRegion(code))
    .filter((r) => r && typeof r.latitude === "number" && typeof r.longitude === "number")
    .map((r) => ({ ...r, ...project(r.latitude, r.longitude), city: r.label.split(",")[0] }));

// The chosen tab, and the arrow keys moving along the strip.
const mcpTab = ref(MCP_SETUPS[0].key);
const stepTab = (by) => {
    const i = MCP_SETUPS.findIndex((m) => m.key === mcpTab.value);
    const next = MCP_SETUPS[(i + by + MCP_SETUPS.length) % MCP_SETUPS.length];
    mcpTab.value = next.key;
    document.getElementById(`mcp-tab-${next.key}`)?.focus();
};

// The download rows, as components/agentPrompts.vue does it: the bytes come
// from the CDN and are saved under the tool's name; a dot-file loses its dot
// in the browser, so the visitor is told to put it back; a failed fetch opens
// the file as SKAPI.md instead.
const downloading = ref("");
async function download(p) {
    if (downloading.value) return;
    downloading.value = p.filename;
    try {
        const saved = await downloadAgentPrompt(p.filename);
        if (saved !== p.filename) {
            showToast(`Saved as ${saved}. Rename it to ${p.filename}: browsers drop the leading dot.`, "info");
        }
    } catch (err) {
        console.warn("[landing] could not fetch the prompt", err);
        window.open(AGENT_PROMPT_URL, "_blank", "noopener");
        showToast(`The file opened as SKAPI.md. Rename it to ${p.filename}.`, "warning");
    } finally {
        downloading.value = "";
    }
}

// ─── glyphs ───
// Feather-style 24x24 glyphs, as data, because a glyph can be drawn twice: in
// its section and on a tile of the hero drawing. A shape
// is [tag, attributes, accent]; an accent shape is stroked in the brand blue.
const ICON = {
    // a person with a check: signed in, verified
    auth: [
        ["path", { d: "M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" }],
        ["circle", { cx: 8.5, cy: 7, r: 4 }],
        ["path", { d: "M17 11l2 2 4-4" }, true],
    ],
    database: [
        ["ellipse", { cx: 12, cy: 5, rx: 9, ry: 3 }, true],
        ["path", { d: "M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" }],
        ["path", { d: "M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" }],
    ],
    // a folder with a file going up into it
    files: [
        ["path", { d: "M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" }],
        ["path", { d: "M12 17v-6" }, true],
        ["path", { d: "M9 14l3-3 3 3" }, true],
    ],
    cdn: [
        ["circle", { cx: 12, cy: 12, r: 10 }],
        ["path", { d: "M2 12h20" }],
        ["path", { d: "M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" }, true],
    ],
    // a broadcast: something live, going out as it happens
    realtime: [
        ["circle", { cx: 12, cy: 12, r: 2 }, true],
        ["path", { d: "M16.24 7.76a6 6 0 0 1 0 8.49m-8.48-.01a6 6 0 0 1 0-8.49m11.31-2.82a10 10 0 0 1 0 14.14m-14.14 0a10 10 0 0 1 0-14.14" }],
    ],
    email: [
        ["path", { d: "M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" }],
        ["path", { d: "M22 6l-10 7L2 6" }, true],
    ],
    // a browser window: the app, live on the web
    hosting: [
        ["rect", { x: 2, y: 3, width: 20, height: 18, rx: 2 }],
        ["path", { d: "M2 8h20" }, true],
        ["path", { d: "M5.5 5.5h.01M8.5 5.5h.01" }],
    ],
    // signing in through a door that is someone else's
    openid: [
        ["path", { d: "M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" }],
        ["path", { d: "M10 17l5-5-5-5" }, true],
        ["path", { d: "M15 12H3" }, true],
    ],
    key: [
        ["path", { d: "M11.39 11.61a5.5 5.5 0 1 1-7.78 7.78 5.5 5.5 0 0 1 7.78-7.78z" }],
        ["path", { d: "M11.39 11.61L19 4M21 2l-2 2" }],
        ["path", { d: "M15.5 7.5l3 3L22 7l-3-3" }, true],
    ],
    // the webhook mark: three hooks passing a call along
    webhooks: [
        ["path", { d: "M18 16.98h-5.99c-1.1 0-1.95.94-2.48 1.9A4 4 0 0 1 2 17c.01-.7.2-1.4.57-2" }],
        ["path", { d: "M6 17l3.13-5.78c.53-.97.1-2.18-.5-3.1a4 4 0 1 1 6.89-4.06" }],
        ["path", { d: "M12 6l3.13 5.73C15.66 12.7 16.9 13 18 13a4 4 0 0 1 0 8" }, true],
    ],
    terminal: [
        ["path", { d: "M4 17l6-6-6-6" }],
        ["path", { d: "M12 19h8" }, true],
    ],
    tokens: [
        ["circle", { cx: 8, cy: 8, r: 6 }, true],
        ["path", { d: "M18.09 10.37A6 6 0 1 1 10.34 18" }],
        ["path", { d: "M7 6h1v4" }, true],
        ["path", { d: "M16.71 13.88l.7.71-2.82 2.82" }],
    ],
    share: [
        ["circle", { cx: 18, cy: 5, r: 3 }],
        ["circle", { cx: 6, cy: 12, r: 3 }, true],
        ["circle", { cx: 18, cy: 19, r: 3 }],
        ["path", { d: "M8.59 13.51l6.83 3.98M15.41 6.51l-6.82 3.98" }],
    ],
    create: [
        ["rect", { x: 3, y: 3, width: 18, height: 18 }],
        ["path", { d: "M12 8v8M8 12h8" }, true],
    ],
    download: [
        ["path", { d: "M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" }],
        // shaft first, so the blue head is drawn over it
        ["path", { d: "M12 3v11" }],
        ["path", { d: "M7 10l5 5 5-5" }, true],
    ],
    link: [
        ["path", { d: "M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" }],
        ["path", { d: "M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" }, true],
    ],
};

// One glyph. On its own it is an svg element; inside the hero drawing it is a
// g (as="g") and inherits the drawing's stroke. The accent is set inline: the
// shapes are made here, outside the template, so they carry no scope attribute
// for a scoped rule to match.
const Glyph = (props, { attrs }) => {
    const shapes = props.shapes.map(([tag, a, accent]) =>
        h(tag, accent ? { ...a, style: { stroke: "var(--bq-pink)" } } : a),
    );
    if (props.as === "g") return h("g", attrs, shapes);
    return h("span", { ...attrs, "aria-hidden": "true" }, [
        h("svg", {
            width: 24,
            height: 24,
            viewBox: "0 0 24 24",
            fill: "none",
            stroke: "currentColor",
            "stroke-width": 2,
            "stroke-linecap": "round",
            "stroke-linejoin": "round",
        }, shapes),
    ]);
};
Glyph.props = ["shapes", "as"];

const FEATURES = [
    { name: "Authentication", desc: "Signup, login, profiles and verification.", icon: ICON.auth },
    { name: "Database", desc: "A schemaless NoSQL database that scales with you.", icon: ICON.database },
    { name: "File Storage", desc: "Store and serve files of any size, with built-in security.", icon: ICON.files },
    { name: "CDN", desc: "Serve your content fast, anywhere in the world.", icon: ICON.cdn },
    { name: "Realtime Data", desc: "Chat, video calls and live notifications, in real time.", icon: ICON.realtime },
    { name: "Automated Emails", desc: "Send newsletters and automated emails to your users.", icon: ICON.email },
    { name: "Web Hosting", desc: "Host your website with zero configuration.", icon: ICON.hosting },
    { name: "OpenID Login", desc: "Let users sign in with Google, Apple and more.", icon: ICON.openid },
    { name: "API Secret Keys", desc: "Call third-party APIs without exposing your secret keys.", icon: ICON.key },
    { name: "Webhooks", desc: "Receive third-party webhooks without running a server.", icon: ICON.webhooks },
];

// ─── the hero drawing ───
// The hero's tag row, in its order. The drawing's tiles carry the same words.
const HERO_TAGS = [
    "Authentication",
    "Database",
    "File Storage",
    "Realtime Data",
    "Automated Email",
    "Web Hosting",
    "OpenID Login",
    "3rd-Party API Integrations",
];

// Four server units in 2:1 isometric projection, stacked on one axis. A unit
// is a rhombus top (half-width W, half-height H) over two side faces T tall.
const CX = 320;
const W = 116;
const H = 58;
const T = 32;
const UNIT_Y = [129, 175, 221, 267];
const pts = (list) => list.map((p) => p.join(",")).join(" ");
const unit = (cx, cy, w, h, t, isTop) => {
    // a point on a side face: s along its length, q down its height (0..1)
    const left = (s, q) => [cx - w + s * w, cy + s * h + q * t];
    const right = (s, q) => [cx + s * w, cy + h - s * h + q * t];
    return {
        isTop,
        top: pts([[cx, cy - h], [cx + w, cy], [cx, cy + h], [cx - w, cy]]),
        left: pts([left(0, 0), left(1, 0), left(1, 1), left(0, 1)]),
        right: pts([right(0, 0), right(1, 0), right(1, 1), right(0, 1)]),
        // three status lights toward the front corner of the left face
        leds: [0.56, 0.69, 0.82].map((s) =>
            pts([left(s, 0.36), left(s + 0.07, 0.36), left(s + 0.07, 0.64), left(s, 0.64)]),
        ),
        // two vent slots along the right face
        vents: [0.34, 0.66].map((q) => [...right(0.2, q), ...right(0.8, q)]),
        // where its two wires land, on the outer edge of each side face
        portL: [cx - w, cy + t / 2],
        portR: [cx + w, cy + t / 2],
    };
};
// A small rhombus on a unit's top face: where something lands on it.
const pad = (cx, cy, w) => pts([[cx, cy - w / 2], [cx + w, cy], [cx, cy + w / 2], [cx - w, cy]]);
// An isometric grid of 2n cells a side, centred on (ox, oy): the floor a
// stack stands on. A mask fades it out toward its edges.
const floor = (ox, oy, n, ax, ay) => {
    const lines = [];
    for (let i = -n; i <= n; i++) {
        lines.push([ox + (i + n) * ax, oy + (i - n) * ay, ox + (i - n) * ax, oy + (i + n) * ay]);
        lines.push([ox + (i - n) * ax, oy + (i - n) * ay, ox + (i + n) * ax, oy + (i + n) * ay]);
    }
    return lines;
};

// Bottom unit first: each one is drawn over the one below it.
const SLABS = UNIT_Y.map((cy, i) => unit(CX, cy, W, H, T, i === 0)).reverse();
const PAD = pad(CX, UNIT_Y[0], 38);
const FLOOR = floor(CX, 300, 6, 26, 13);

// The steps' drawing: two units under a web page.
const MINI_SLABS = [unit(130, 330, 84, 42, 24, false), unit(130, 296, 84, 42, 24, true)];
const MINI_PAD = pad(130, 296, 26);
const MINI_FLOOR = floor(130, 350, 4, 20, 10);

// The MCP drawing's forms: three of the dashboard pages the server fills in.
const MCP_TILES = [
    { x: 64, icon: ICON.webhooks, label: "Tickets" },
    { x: 130, icon: ICON.key, label: "Secret keys" },
    { x: 196, icon: ICON.auth, label: "Users" },
];

// What the MCP window types: the dashboard work the server does on a prompt.
// Up to 28 characters a line, the window's width at its text size.
const MCP_PROMPTS = [
    ["Save this API key as a", "secret named weather_api"],
    ["Register a ticket for the", "payment webhook"],
    ["Invite a new admin to", "my project"],
    ["Make every record in the", "posts table private"],
    ["Set up Google sign-in,", "@mycompany.com only"],
    ["Upload this page to", "my project's website"],
    ["Write a welcome e-mail and", "upload it as the template"],
    ["Show me what my orders", "ticket received today"],
    ["Turn off sign-ups until", "the launch"],
    ["List my projects and", "use the shop one"],
];

// Eight tiles, four a side, one per hero tag, each wired to its unit: out
// from the tile, a jog up or down, then in to the unit's port. The jogs are
// staggered so no two wires cross.
const TILE_Y = [46, 158, 270, 382];
const JOG = [176, 150, 150, 176];
const TILE_ICON = ["auth", "database", "files", "realtime", "email", "hosting", "openid", "key"];
const TILES = HERO_TAGS.map((tag, i) => ({
    x: i < 4 ? 76 : 564,
    y: TILE_Y[i % 4],
    icon: ICON[TILE_ICON[i]],
    // the one tag too long for a tile's width takes two lines
    label: tag === "3rd-Party API Integrations" ? ["3rd-Party API", "Integrations"] : [tag],
}));
const WIRES = TILES.map((t, i) => {
    const row = i % 4;
    const portY = UNIT_Y[row] + T / 2;
    return i < 4
        ? { d: `M104 ${t.y}H${JOG[row]}V${portY}H${CX - W}`, out: false }
        : { d: `M536 ${t.y}H${640 - JOG[row]}V${portY}H${CX + W}`, out: true };
});

// The serverless drawing's row of nodes.
const NODE_X = [50, 80, 110, 140, 170, 200, 230];

// ─── the terminal's prompts ───
// What a visitor might type first. Demo filler, not site copy: two lines at
// most, 23 characters a line, so a line and its cursor fit the window at the
// drawing's 11px. Typed one character at a time, left to right, read for a
// moment, then the prompts rotate through a shuffled cycle. With reduced
// motion the first prompt simply stands there.
const PROMPTS = [
    ["Build a team task board", "with shared task lists"],
    ["Build Google sign-in", "with user profiles"],
    ["Build a blog platform", "with posts and tags"],
    ["Build a signup flow", "with welcome emails"],
    ["Build a contact form", "with email alerts"],
    ["Build a photo gallery", "with likes and comments"],
    ["Build a video chat app", "for remote teams"],
    ["Build secure storage", "for office files"],
    ["Build an order system", "for restaurant tables"],
    ["Build customer chat", "with private threads"],
    ["Build an online shop", "with secure checkout"],
    ["Build concert ticketing", "with attendee check-in"],
];
const TYPE_MS = 42;        // one character
const LINE_PAUSE_MS = 260; // before the second line starts
const READ_MS = 2600;      // the finished prompt stays this long
const reduceMotion = typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// A window that types its prompts one after another, the list in a random
// order each time round, never the same prompt twice in a row. The hero's
// window and the MCP drawing's are two of these, each with its own list.
const makeTyper = (prompts) => {
    const typed = ref(["", ""]);
    const cursorLine = ref(0);
    let timer = null;
    let queue = [];
    const next = (previous = -1) => {
        if (!queue.length) {
            queue = prompts.map((_, index) => index);
            for (let i = queue.length - 1; i > 0; i--) {
                const j = Math.floor(Math.random() * (i + 1));
                [queue[i], queue[j]] = [queue[j], queue[i]];
            }
            if (queue[0] === previous) {
                [queue[0], queue[1]] = [queue[1], queue[0]];
            }
        }
        return queue.shift();
    };
    const type = (index) => {
        const lines = prompts[index];
        typed.value = ["", ""];
        cursorLine.value = 0;
        let line = 0;
        let chars = 0;
        const step = () => {
            if (chars < lines[line].length) {
                chars++;
                typed.value[line] = lines[line].slice(0, chars);
                timer = setTimeout(step, TYPE_MS);
                return;
            }
            if (line + 1 < lines.length) {
                line++;
                chars = 0;
                cursorLine.value = line;
                timer = setTimeout(step, LINE_PAUSE_MS);
                return;
            }
            timer = setTimeout(() => type(next(index)), READ_MS);
        };
        step();
    };
    // with reduced motion the first prompt stands, finished, and nothing types
    const start = () => {
        if (reduceMotion) {
            typed.value = [...prompts[0]];
            cursorLine.value = prompts[0].length - 1;
        } else {
            type(next());
        }
    };
    const stop = () => clearTimeout(timer);
    return { typed, cursorLine, start, stop };
};

const heroTerm = makeTyper(PROMPTS);
const { typed, cursorLine } = heroTerm; // the hero window's lines, as its markup reads them
const mcpTerm = makeTyper(MCP_PROMPTS);
const { typed: mcpTyped, cursorLine: mcpCursorLine } = mcpTerm;

// The hero drawing's phone layout (see the viewBox binding): one breakpoint,
// the stylesheet's 640px, read from the browser so the two never disagree.
const phone = ref(false);
const phoneQuery = typeof window !== "undefined" ? window.matchMedia("(max-width: 640px)") : null;
const readPhone = () => { phone.value = !!phoneQuery?.matches; };

onMounted(() => {
    readPhone();
    phoneQuery?.addEventListener("change", readPhone);
    heroTerm.start();
    mcpTerm.start();
    removeFullScreenLoader();
    getArticles()
        .then(({ articles }) => { newsArticles.value = articles.slice(0, 3); })
        .catch(() => { /* section stays hidden */ });
});

onBeforeUnmount(() => {
    phoneQuery?.removeEventListener("change", readPhone);
    heroTerm.stop();
    mcpTerm.stop();
});
</script>

<style lang="less" scoped>
@import "@/assets/less/bq-tokens.less";
@import (reference) "@/assets/less/bq-list-page.less";

/* One step off the paper, for the shaded face of a drawn solid. */
@shade: color-mix(in srgb, @line 60%, @paper);
/* The isometric grid the hero's stack stands on, as a background: two sets of
   hairlines at the 2:1 slope. */
@iso-angle: 26.565deg;
.iso-grid(@size: 30px) {
    background-image:
        repeating-linear-gradient(@iso-angle, @line 0, @line 1px, transparent 1px, transparent @size),
        repeating-linear-gradient(-@iso-angle, @line 0, @line 1px, transparent 1px, transparent @size);
}

.bq-landing {
    min-height: 100vh;
    margin: 0;
    background: @paper;
    color: @ink;
    font-family: @mono;
    font-size: 16px;
    line-height: 1.7;
    overflow-x: hidden;

    hr {
        background: @line;
    }
}

/* Sections are full-bleed so a band can carry its own surface; the content
   column, the navbar's width, lives in .bq-wrap. */
.bq-wrap {
    max-width: 1116px;
    margin: 0 auto;
    padding: 0 2rem;
}

/* Visually hidden, still read by screen readers and crawlers. */
.bq-sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    margin: -1px;
    padding: 0;
    border: 0;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
}

/* ─── button ─── */
/* The landing's block button. Hover lift and press come from the global
   a.bq-btn rules in base.less; its colours never change on hover. */
.bq-btn {
    display: inline-block;
    padding: 0.9rem 1.75rem;
    border: 1px solid @btn-bg;
    background: @btn-bg;
    color: @paper;
    font: inherit;
    font-size: 0.92rem;
    letter-spacing: 0.01em;
    text-decoration: none;
    cursor: pointer;
    transition: transform 0.12s ease, box-shadow 0.12s ease;
    box-shadow: 2px 2px var(--bq-shadow);
}

/* ─── hero ─── */
/* The brand tint (@pink-bg), strongest under the navbar and gone by the
   hero's bottom edge, with a dot grid laid over it that fades out the same
   way. Both sit behind the content. */
.bq-hero {
    position: relative;
    isolation: isolate;
    padding: 4.5rem 0 4rem;
    background: linear-gradient(@pink-bg, transparent);

    &::before {
        content: "";
        position: absolute;
        inset: 0;
        z-index: -1;
        pointer-events: none;
        background-image: radial-gradient(@line 1px, transparent 1.5px);
        background-size: 24px 24px;
        background-position: center top;
        -webkit-mask-image: linear-gradient(#000 30%, transparent);
        mask-image: linear-gradient(#000 30%, transparent);
    }
}

.bq-hero-grid {
    display: grid;
    /* minmax(0, ...) so neither column can be widened by its content */
    grid-template-columns: minmax(0, 0.92fr) minmax(0, 1.08fr);
    gap: 2.5rem;
    align-items: center;
}

/* The banner carries its own look; the hero only places it. Deeper than the
   component's own rule so it wins wherever the stylesheets land. */
.bq-hero .bq-ascii {
    display: block;
    margin: 0;
    /* The art is 44 columns of about 0.65em, 28.4em in all, and has to fit
       the copy column: 46% of the wrap less its gutters and the grid gap. */
    font-size: min(1rem, calc((100vw - 104px) / 62));
}

.bq-hero-tagline {
    margin: 2.25rem 0 0;
    font-size: clamp(1.2rem, 2.1vw, 1.55rem);
    line-height: 1.4;
    font-weight: 600;
    text-shadow: var(--bq-text-shadow);
}

.bq-hero-sub {
    margin: 0.9rem 0 0;
    max-width: 30rem;
    color: @muted;
    font-size: 1.02rem;
}

/* Screen readers only while the drawing's labels are readable; shown as a
   row of chips at phone width (see the responsive block). */
.bq-hero-tags {
    position: absolute;
    width: 1px;
    height: 1px;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
}

.bq-hero-tag {
    padding: 0.3rem 0.7rem;
    border: 1px solid @line;
    background: @paper;
    color: @muted;
    font-size: 0.8rem;
    white-space: nowrap;
}

/* the button, and the documentation link on its own line under it */
.bq-hero-actions {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 1.1rem;
    margin-top: 2.25rem;
}

.bq-hero-install {
    display: inline-block;
    max-width: 100%;
    margin-top: 2rem;
}

/* ─── the hero drawing ─── */
.bq-hero-art {
    min-width: 0;
}

.bq-art {
    display: block;
    width: 100%;
    height: auto;
    overflow: visible;
    stroke-linecap: round;
    stroke-linejoin: round;
}

.bq-art-floor line {
    stroke: @line;
    stroke-width: 1;
}

.bq-art-wire {
    stroke: @line;
    stroke-width: 1.5;
}

/* Short dashes running along the wire: in toward the stack on the left, out
   from it on the right. */
.bq-art-flow {
    stroke: @pink;
    stroke-width: 2;
    stroke-dasharray: 3 21;
    animation: bq-flow 1.6s linear infinite;
}

.bq-art-flow--out {
    animation-direction: reverse;
}

@keyframes bq-flow {
    to {
        stroke-dashoffset: -24;
    }
}

.bq-art-slab polygon {
    stroke: @ink;
    stroke-width: 1.5;
}

.bq-art-face-top {
    fill: @paper;
}

.bq-art-face-left {
    fill: @hover-bg;
}

.bq-art-face-right {
    fill: @shade;
}

.bq-art-slab--top .bq-art-face-top {
    fill: @pink-bg;
}

.bq-art-slab .bq-art-led {
    fill: @pink;
    stroke: none;
    animation: bq-blink 2.4s steps(1, end) infinite;

    &:nth-of-type(5) {
        animation-delay: 0.8s;
    }

    &:nth-of-type(6) {
        fill: @muted;
        animation: none;
    }
}

/* the units do not blink in step */
.bq-art-slab:nth-of-type(odd) .bq-art-led {
    animation-duration: 3.1s;
}

@keyframes bq-blink {
    0%,
    100% {
        opacity: 1;
    }

    70% {
        opacity: 0.25;
    }
}

.bq-art-vent {
    stroke: @ink;
    stroke-width: 1.5;
    opacity: 0.45;
}

.bq-art-port {
    fill: @pink;
}

.bq-art-pad {
    stroke: @pink;
    stroke-width: 1.5;
    stroke-dasharray: 4 4;
}

.bq-art-beam {
    stroke: @pink;
    stroke-width: 1.5;
    stroke-dasharray: 2 5;
}

/* the agent's window hovers over the stack, and the mark over the wire */
.bq-art-term--bob,
.bq-art-logo {
    animation: bq-bob 4.5s ease-in-out infinite;
}

@keyframes bq-bob {
    0%,
    100% {
        transform: translateY(0);
    }

    50% {
        transform: translateY(-6px);
    }
}

.bq-art-tile-shadow {
    fill: var(--bq-shadow);
}

.bq-art-tile-box {
    fill: @paper;
    stroke: @line;
    stroke-width: 1;
}

.bq-art-tile g {
    stroke: @ink;
    stroke-width: 2;
}

.bq-art-label {
    fill: @muted;
    font-family: @mono;
    font-size: 13.5px;
    text-anchor: middle;
}

/* the MCP drawing: the server's name in the node, and the small captions */
.bq-art-node-text {
    fill: @pink;
    font-family: @mono;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.1em;
    text-anchor: middle;
}

.bq-art-caption {
    fill: @muted;
    font-family: @mono;
    font-size: 10px;
    text-anchor: middle;
}

/* ─── sections ─── */
.bq-section {
    padding: 6rem 0;
}

/* The hero's fade, at under half strength: a whisper of the brand tint under
   the separator, gone by the bottom, so the page ends the way it began
   without the pricing cards sitting on a visible blue band. */
.bq-section--soft {
    
    background: color-mix(in srgb, @pink-bg 40%, @paper); //linear-gradient(color-mix(in srgb, @pink-bg 40%, @paper), transparent);
}

.bq-section-top {
    border-top: 1px solid @line;
}
.bq-section-bottom {
    border-bottom: 1px solid @line;
}

/* the hero's background upside down: plain at the top, the blue tint growing
   toward the bottom (dark, then blue, in dark mode) */
.bq-section--soft-up {
    position: relative;
    isolation: isolate;
    background: linear-gradient(transparent, @pink-bg);

    /* the hero's dots, upside down too: faint at the top, full where the blue is */
    &::before {
        content: "";
        position: absolute;
        inset: 0;
        z-index: -1;
        pointer-events: none;
        background-image: radial-gradient(@line 1px, transparent 1.5px);
        background-size: 24px 24px;
        background-position: center bottom;
        -webkit-mask-image: linear-gradient(transparent, #000 70%);
        mask-image: linear-gradient(transparent, #000 70%);
    }
}

/* Title on the left, the supporting line on the right, level with the
   title's last line. */
.bq-section-header {
    display: grid;
    grid-template-columns: minmax(0, 1.1fr) minmax(0, 1fr);
    gap: 1rem 3.5rem;
    align-items: end;
    margin-bottom: 3.25rem;

    h2 {
        margin: 0;
        font-size: clamp(1.8rem, 3.5vw, 2.7rem);
        line-height: 1.12;
        font-weight: 700;
        letter-spacing: -0.01em;
        text-shadow: var(--bq-text-shadow);
    }
}

.bq-section-sub {
    margin: 0 0 0.3rem;
    color: @muted;
    line-height: 1.7;
}

.bq-section-link {
    margin: 2rem 0 0;
    font-size: 0.9rem;
}

/* ─── glyphs ─── */
/* Bare decorative marks. No box, border, or shadow: a framed tile read as a
   button, and these are not clickable. */
.bq-icon {
    display: inline-flex;
    flex-shrink: 0;
    color: @ink;

    :deep(svg) {
        display: block;
    }
}

/* ─── feature grid ─── */
/* Ruled like a table: the list draws the top and left edges, each cell its
   right and bottom, so the rules never double up whatever the column count. */
.bq-feature-grid {
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    margin: 0;
    padding: 0;
    list-style: none;
    border-top: 1px solid @line;
    border-left: 1px solid @line;
    background: @paper;
    box-shadow: 4px 4px var(--bq-shadow);
}

.bq-feature {
    position: relative;
    display: flex;
    flex-direction: column;
    min-width: 0;
    padding: 1.6rem 1.25rem 1.75rem;
    border-right: 1px solid @line;
    border-bottom: 1px solid @line;

    /* a short brand-blue tick on the cell's top rule */
    &::before {
        content: "";
        position: absolute;
        top: -1px;
        left: 1.25rem;
        width: 1.5rem;
        border-top: 2px solid @pink;
    }

    .bq-icon :deep(svg) {
        width: 28px;
        height: 28px;
    }
}

.bq-feature-name {
    display: block;
    margin-top: 1rem;
    font-weight: 600;
    font-size: 0.98rem;
    line-height: 1.4;
}

.bq-feature-desc {
    display: block;
    margin-top: 0.4rem;
    color: @muted;
    font-size: 0.85rem;
    line-height: 1.6;
}

/* ─── why ─── */
/* four reasons, two by two; one column on a phone (responsive block) */
.bq-why-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 1.5rem;
}

.bq-why {
    display: flex;
    flex-direction: column;
    min-width: 0;
    border: 1px solid @line;
    background: @paper;
    box-shadow: 2px 2px var(--bq-shadow);
}

/* The drawing's own panel: the tint and the dot grid of the hero. */
.bq-why-art {
    padding: 1.25rem 1rem;
    border-bottom: 1px solid @line;
    background-color: @pink-bg;
    background-image: radial-gradient(@line 1px, transparent 1.5px);
    background-size: 16px 16px;

    svg {
        display: block;
        width: 100%;
        max-width: 300px;
        height: auto;
        margin: 0 auto;
        stroke-linecap: round;
        stroke-linejoin: round;
    }
}

.bq-why-body {
    padding: 1.6rem 1.5rem 1.75rem;

    h3 {
        margin: 0 0 0.6rem;
        font-size: 1.08rem;
        font-weight: 600;
        line-height: 1.4;
    }

    p {
        margin: 0;
        color: @muted;
        font-size: 0.93rem;
        line-height: 1.65;
    }
}

/* the drawings' vocabulary */
.ill-line {
    stroke: @muted;
    stroke-width: 1;
    opacity: 0.6;
}

.ill-ink {
    stroke: @ink;
    stroke-width: 1.5;
}

.ill-surface {
    fill: @paper;
    stroke: @ink;
    stroke-width: 1.5;
}

/* inside a scaled group the stroke keeps its own width */
.ill-scaled {
    vector-effect: non-scaling-stroke;
}

.ill-accent {
    stroke: @pink;
    stroke-width: 2;
}

.ill-dash {
    stroke-width: 1.5;
    stroke-dasharray: 4 4;
}

.ill-flow {
    stroke-dasharray: 3 9;
    animation: bq-flow-short 1.2s linear infinite;
}

@keyframes bq-flow-short {
    to {
        stroke-dashoffset: -12;
    }
}

.ill-block {
    fill: @paper;
    stroke: @ink;
    stroke-width: 1.5;
}

.ill-block--accent {
    fill: @pink;
    stroke: @pink;
}

.ill-cipher {
    fill: @ink;
    stroke: @pink-bg;
    stroke-width: 3;
}

.ill-cipher--accent {
    fill: @pink;
}

.ill-node {
    fill: @paper;
    stroke: @ink;
    stroke-width: 1.5;
}

.ill-node--ghost {
    stroke: @pink;
    stroke-dasharray: 3 3;
}

/* ─── cards (agents, news) ─── */
.bq-card-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 1.5rem;
}

.bq-card {
    /* the card itself is the anchor; a column so the link line sits at the
       bottom whatever the length of the copy above it */
    display: flex;
    flex-direction: column;
    min-width: 0;
    padding: 2rem 1.75rem 1.75rem;
    border: 1px solid @line;
    border-top: 2px solid @pink;
    background: @paper;
    box-shadow: 2px 2px var(--bq-shadow);
    color: @ink;
    text-decoration: none;
    transition: transform 0.12s ease, box-shadow 0.12s ease;

    &:hover {
        text-decoration: none;
    }

    /* the lift only where hover is a real pointer state */
    @media (hover: hover) and (pointer: fine) {
        &:hover {
            transform: translate(-1px, -1px);
            box-shadow: 4px 4px var(--bq-shadow);
        }
    }

    .bq-icon {
        margin-bottom: 1.1rem;

        :deep(svg) {
            width: 28px;
            height: 28px;
        }
    }

    h3 {
        margin: 0 0 0.6rem;
        font-size: 1.1rem;
        line-height: 1.3;
    }

    p {
        margin: 0;
        color: @muted;
        font-size: 0.95rem;
        line-height: 1.6;
    }
}

/* A span, since the card is already the anchor. Pinned to the card's bottom
   edge so the three link lines sit level. */
.bq-card-more {
    margin-top: auto;
    padding-top: 1.5rem;
    color: @pink;
    font-size: 0.9rem;
}

.bq-news-date {
    margin-bottom: 0.6rem;
    color: @muted;
    font-size: 0.78rem;
}

.bq-news-excerpt {
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

/* ─── steps ─── */
/* A rail down the left, a boxed glyph at each step, the recipe beside it. */
.bq-how-grid {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 270px;
    gap: 1rem;
    align-items: center;
}

.bq-how-art {
    min-width: 0;
}

.bq-steps {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    margin: 0;
    padding: 0;
    list-style: none;
}

.bq-step {
    position: relative;
    display: flex;
    gap: 1.75rem;
    align-items: flex-start;
    /* a grid item's min-width defaults to its content; the pre-formatted code
       would otherwise force the row wider than the container */
    min-width: 0;
    padding-bottom: 2.75rem;

    &:last-child {
        padding-bottom: 0;
    }

    /* the rail, from under this node to the next one */
    &:not(:last-child)::before {
        content: "";
        position: absolute;
        top: 3rem;
        bottom: 0;
        left: calc(1.5rem - 1px);
        border-left: 2px dotted @pink;
    }

    h3 {
        margin: 0 0 0.45rem;
        font-size: 1.15rem;
        font-weight: 600;
    }

    p {
        margin: 0;
        max-width: 76ch;
        color: @muted;
        font-size: 0.95rem;
        line-height: 1.6;
    }
}

.bq-step-node {
    position: relative;
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: 3rem;
    height: 3rem;
    border: 1px solid @line;
    background: @paper;
    box-shadow: 2px 2px var(--bq-shadow);
}

.bq-step-body {
    flex: 1;
    min-width: 0;
}

/* "Step n" as a small brand-coloured label, in the register of the pills */
.bq-step-label {
    display: block;
    margin-bottom: 0.2rem;
    color: @pink;
    font-size: 0.72rem;
    font-weight: 600;
    letter-spacing: 0.1em;
    text-transform: uppercase;
}

/* ─── code samples ─── */
/* The box IS CopyText's root span (fallthrough class), so a click anywhere in
   it copies. The .bq-landing prefix outranks CopyText's own scoped rules.
   The inner code element is the one that scrolls, so the corner glyph stays
   put while a long line pans. */
.bq-code-option {
    margin-top: 0.85rem;
}

.bq-code-caption {
    display: block;
    color: @muted;
    font-size: 0.72rem;
    letter-spacing: 0.08em;
    text-transform: uppercase;
}

.bq-landing .bq-code {
    position: relative;
    display: block;
    max-width: 100%;
    min-width: 0;
    margin-top: 0.35rem;
    padding: 0;
    border: 1px solid @input-line;
    background: @paper;
    box-shadow: 2px 2px var(--bq-shadow);
    color: @ink;
    font-family: @mono;
    font-size: 0.82rem;
    line-height: 1.5;
    text-align: left;

    &:hover {
        text-decoration: none;
    }

    code {
        display: block;
        max-width: 100%;
        padding: 0.75rem 2.6rem 0.75rem 1rem;
        font: inherit;
        white-space: pre;
        overflow-x: auto;
        scrollbar-width: thin;
    }

    /* the glyph is always visible and lights up in the brand colour on hover */
    &:hover .bq-code-copy {
        color: @pink;
    }
}

.bq-code-copy {
    position: absolute;
    top: 0.6rem;
    right: 0;
    /* opaque on the box's paper, out to the box's edge, so scrolled code
       never shows through or beside it */
    padding: 0 0.6rem 0 0.4rem;
    background: @paper;
    line-height: 1;
    font-size: 1.1rem;
    color: @muted;
    transition: color 0.12s ease;
}

.bq-code-or {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin: 0.85rem 0;
    color: @muted;
    font-size: 0.78rem;

    &::before,
    &::after {
        content: "";
        flex: 1;
        border-top: 1px solid @line;
    }
}

/* Syntax colours for the samples, drawn from the existing status palette so
   they hold up in both schemes: keywords info blue, tag and class names the
   brand blue, attribute names amber, strings green, punctuation muted. The
   markup is display only; CopyText still copies the plain string. */
.tok-kw {
    color: @info;
}

.tok-tag,
.tok-cls {
    color: @pink;
}

.tok-cls {
    font-weight: 600;
}

.tok-attr {
    color: @warning;
}

.tok-str {
    color: @success;
}

.tok-p {
    color: @muted;
}

/* ─── pricing ─── */
/* The plan cards are PlanCards' own markup. The landing restyles them into its
   card language (paper, hairline, 2px offset shadow, the same lift on hover as
   the agent and news cards) and makes each one a single link. */
.bq-plans.bq-pricing-cards {
    max-width: none;
    /* the component clips its overflow, which would cut off the hover shadow */
    overflow: visible;
    /* room for the POPULAR badge on the Premium card's top edge */
    padding-top: 0.75rem;

    :deep(.bq-plans-grid) {
        gap: 1.5rem;
    }

    :deep(.bq-plan-card) {
        /* the anchor for the stretched link below */
        position: relative;
        background: @paper;
        box-shadow: 2px 2px var(--bq-shadow);
        transition: transform 0.12s ease, box-shadow 0.12s ease, border-color 0.15s;
    }

    /* the whole card is the link: the action link stretches over its card
       (the card is the positioned ancestor), and the note links in the spec
       list stay above it. A click anywhere on the card is then the link's,
       and its data-track counts it. */
    :deep(.bq-plan-action),
    :deep(.bq-plan-link) {
        position: static;
    }

    :deep(.bq-plan-link)::after {
        content: "";
        position: absolute;
        inset: 0;
    }

    :deep(.bq-plan-card a:not(.bq-plan-link)) {
        position: relative;
        z-index: 1;
    }

    @media (hover: hover) and (pointer: fine) {
        :deep(.bq-plan-card:hover) {
            transform: translate(-1px, -1px);
            box-shadow: 4px 4px var(--bq-shadow);
        }

        /* the outlined button of a hovered card turns brand blue, text and edge */
        :deep(.bq-plan-card:hover) .bq-plan-cta--ghost {
            border-color: @pink;
            color: @pink;
        }
    }

    :deep(.bq-plan-card:active) {
        transform: translate(1px, 1px);
        box-shadow: 1px 1px var(--bq-shadow);
        transition-duration: 0.04s;
    }
}

/* The card's one link: its ::after covers the whole card. The visible button
   is a span inside it, not the anchor, because the global a.bq-btn hover
   transforms the anchor and would shrink its ::after to the button's box. */
.bq-plan-link {
    display: block;
    color: inherit;
    text-decoration: none;

    &:hover {
        text-decoration: none;
    }

    &::after {
        content: "";
        position: absolute;
        inset: 0;
        z-index: 1;
    }

    /* the focus ring goes round the card, which is what the link stands for */
    &:focus-visible {
        outline: none;
    }

    &:focus-visible::after {
        outline: 2px solid @pink;
        outline-offset: 3px;
    }
}

.bq-plan-cta {
    display: block;
    width: 100%;
    box-sizing: border-box;
    padding: 0.8rem 1rem;
    text-align: center;
    white-space: nowrap;
}

/* Free and Standard: outlined, so Premium's filled button stands out. */
.bq-plan-cta--ghost {
    background: @paper;
    border-color: @input-line;
    color: @ink;
}

/* ─── closing panel ─── */
/* no rule above it: the pricing wash fades straight into this section */

.bq-close-panel {
    position: relative;
    isolation: isolate;
    padding: 4.5rem 1.5rem 5rem;
    border: 1px solid @line;
    background: @pink-bg;
    box-shadow: 4px 4px var(--bq-shadow);
    text-align: center;
    overflow: hidden;

    /* the floor grid, clear in the middle where the words are */
    &::before {
        content: "";
        position: absolute;
        inset: 0;
        z-index: -1;
        pointer-events: none;
        .iso-grid(34px);
        opacity: 0.7;
        -webkit-mask-image: radial-gradient(60% 75% at 50% 50%, transparent 35%, #000);
        mask-image: radial-gradient(60% 75% at 50% 50%, transparent 35%, #000);
    }

    /* the mark above the closing line; width/height on the tag only lend the
       box its aspect ratio */
    img {
        display: block;
        width: 6.22rem;
        height: auto;
        margin: 0 auto 1.5rem;
    }

    h2 {
        margin: 0 0 0.85rem;
        font-size: clamp(1.9rem, 3.8vw, 2.8rem);
        line-height: 1.15;
        font-weight: 700;
        text-shadow: var(--bq-text-shadow);
    }

    p {
        margin: 0 0 2.25rem;
        color: @muted;
    }
}

/* ─── section backgrounds ─── */
/* Graph paper behind the reasons: a square grid of the page's rule colour,
   clearest where the cards are and fading out toward the section's edges.
   Drawn from the theme tokens, so it follows light and dark. */
.bq-section--grid {
    position: relative;
    isolation: isolate;
    overflow: hidden;

    &::before {
        content: "";
        position: absolute;
        inset: 0;
        z-index: -1;
        pointer-events: none;
    }
}

/* the rules in the brand blue, faint, so the paper reads blue rather than grey */
.bq-section--grid::before {
    background-image:
        linear-gradient(to right, @pink 1px, transparent 1px),
        linear-gradient(to bottom, @pink 1px, transparent 1px);
    background-size: 48px 48px;
    background-position: center top;
    opacity: 0.16;
    -webkit-mask-image: radial-gradient(75% 85% at 50% 50%, #000 30%, transparent);
    mask-image: radial-gradient(75% 85% at 50% 50%, #000 30%, transparent);
}

/* ─── the headline ─── */
.bq-hero-title {
    margin: 2rem 0 0;
    font-size: clamp(1.7rem, 3.1vw, 2.35rem);
    line-height: 1.15;
    font-weight: 700;
    letter-spacing: -0.01em;
    text-shadow: var(--bq-text-shadow);
}

/* ─── the agent's terminal in the drawings ─── */
.bq-art-term-box {
    fill: @paper;
    stroke: @ink;
    stroke-width: 1.5;
}

.bq-art-term-bar {
    stroke: @ink;
    stroke-width: 1.5;
}

.bq-art-term-dot {
    fill: @muted;
}

/* the MCP drawing's window and node: a thin, quieter edge than the hero's */
.bq-art-box--light {
    stroke: @muted;
    stroke-width: 1;
}

.bq-art-term-text {
    font-family: @mono;
    font-size: 11px;
    fill: @ink;
}

.bq-art-cursor {
    fill: @pink;
    animation: bq-blink 1s steps(1, end) infinite;
}

/* the window's lines are a line box each; the beam and the wire pass behind them */
.bq-art-term-text {
    pointer-events: none;
}

@keyframes bq-blink {
    50% {
        opacity: 0;
    }
}

/* ─── the base prompt file names ─── */
.bq-file-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 0.4rem;
    margin-top: 1rem;
}

.bq-file-chip {
    padding: 0.2rem 0.55rem;
    border: 1px solid @line;
    background: @paper;
    color: @muted;
    font-family: @mono;
    font-size: 0.74rem;
    white-space: nowrap;
}

/* a card's one figure: the number in ink, the rest muted */
.bq-card-figure {
    margin-top: 1rem;
    color: @muted;
    font-size: 0.88rem;
    line-height: 1.55;

    strong {
        color: @ink;
        font-weight: 600;
    }
}

/* the base prompt rows inside their step; one column, so a tool's name is
   never cut short to fit beside another's */
.bq-prompt-list {
    margin: 1rem 0 0;
    padding: 0;
    border-bottom: 1px solid @line;
    list-style: none;
}

.bq-prompt-row {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    min-width: 0;
    padding: 0.6rem 0;
    border-top: 1px solid @line;
}

.bq-prompt-logo {
    flex: none;
    width: 18px;
    height: 18px;
    fill: currentColor;
}

.bq-prompt-tool {
    flex: 1;
    min-width: 0;
    font-size: 0.9rem;
}

.bq-prompt-get {
    flex: none;
    white-space: nowrap;

    &.disabled {
        pointer-events: none;
        opacity: 0.6;
    }
}

.bq-step-or {
    margin-top: 1.25rem;
}

/* a step's own link, under its text */
.bq-step .bq-step-link {
    margin-top: 0.85rem;
    font-size: 0.9rem;
}

/* a step rail on its own, with no drawing beside it: one width for every
   section that uses it, so its code boxes line up from section to section */
.bq-steps--wide {
    max-width: 58rem;
}

/* a section's longer explanation, under the header, when the sub beside the
   heading has to stay short */
.bq-section-intro {
    max-width: 58rem;
    margin: -1.5rem 0 2.5rem;
    color: @muted;
    line-height: 1.7;
}

/* ─── the map ─── */
.bq-map {
    padding: 1.5rem 1.25rem 1rem;
    border: 1px solid @line;
    background: @paper;
    box-shadow: 2px 2px var(--bq-shadow);
}

.bq-map-track {
    display: flex;
    width: 100%;
}

.bq-map-svg {
    display: block;
    width: 100%;
    height: auto;
    overflow: visible;
}

/* the second copy exists for the phone's loop only */
.bq-map-copy--2 {
    display: none;
}

@keyframes bq-pan {
    from {
        transform: translateX(0);
    }

    to {
        transform: translateX(-760px);
    }
}

/* the land: zero-length strokes with round caps, one dot each */
.bq-map-dots {
    stroke: @muted;
    stroke-opacity: 0.45;
    stroke-width: 2.6;
    stroke-linecap: round;
}

.bq-map-spot {
    fill: @pink;
}

/* a ring breathing out of each pin */
.bq-map-ring {
    fill: none;
    stroke: @pink;
    stroke-width: 1.5;
    transform-box: fill-box;
    transform-origin: center;
    animation: bq-ping 2.6s ease-out infinite;
}

@keyframes bq-ping {
    0% {
        transform: scale(0.4);
        opacity: 0.9;
    }

    100% {
        transform: scale(1.6);
        opacity: 0;
    }
}

.bq-map-label {
    fill: @ink;
    font-family: @mono;
    font-size: 11px;
    font-weight: 600;
    text-anchor: middle;
    paint-order: stroke;
    stroke: @paper;
    stroke-width: 3px;
    stroke-linejoin: round;
}

/* the regions by name, under the map */
.bq-map-list {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem 1.5rem;
    margin: 1.25rem 0 0;
    padding: 0;
    list-style: none;
    color: @muted;
    font-size: 0.9rem;

    li::before {
        content: "";
        display: inline-block;
        width: 7px;
        height: 7px;
        margin-right: 0.5rem;
        background: @pink;
        vertical-align: 1px;
    }
}

/* ─── the two ways into the codebase ─── */
.bq-ways {
    min-width: 0;
}

/* a panel per way, in the register of the cards: no node, no rail */
.bq-way {
    min-width: 0;
    padding: 1.5rem 1.6rem 1.6rem;
    border: 1px solid @line;
    background: @paper;
    box-shadow: 2px 2px var(--bq-shadow);

    h3 {
        margin: 0.35rem 0 0.45rem;
        font-size: 1.15rem;
        font-weight: 600;
    }

    p {
        margin: 0;
        color: @muted;
        font-size: 0.95rem;
        line-height: 1.6;
    }
}

/* the "or" rule between the two panels */
.bq-ways .bq-code-or {
    margin: 1rem 0;
}

.bq-way .bq-step-link {
    margin-top: 0.85rem;
}

/* ─── the MCP server ─── */
/* The tab strip is the dashboard's own (mail, newsletter, tickets: bq-list-page.less),
   pulled in by reference so the chosen tab looks the same everywhere: outlined pill,
   blue and a little bold when active. Only the logo and the placement are this page's. */
.bq-tabs {
    &:extend(.bq-tabs all);
    gap: 0.4rem;
    margin: 1rem 0 0;
}

/* a little tighter than the dashboard's, so six fit the rail on one row */
.bq-tab {
    &:extend(.bq-tab all);
    gap: 0.4rem;
    padding: 0.4rem 0.7rem;
}

/* the narrow picker: shown in place of the tabs under 1000px. The logo and
   the arrow sit over the select and let clicks through to it. */
.bq-tab-select {
    position: relative;
    display: none;
    margin: 1rem 0 0;

    select {
        width: 100%;
        padding-left: 2.7rem;
        padding-right: 2.7rem;
        background-image: none;
        box-shadow: 2px 2px var(--bq-shadow);
    }
}

.bq-tab-select-logo {
    position: absolute;
    top: 50%;
    left: 0.95rem;
    width: 18px;
    height: 18px;
    margin-top: -9px;
    fill: currentColor;
    color: @ink;
    pointer-events: none;
}

.bq-tab-select-arrow {
    position: absolute;
    top: 50%;
    right: 0.55rem;
    transform: translateY(-50%);
    color: @ink;
    font-size: 1.6rem;
    line-height: 1;
    pointer-events: none;
}

.bq-tab-logo {
    flex: none;
    width: 16px;
    height: 16px;
    fill: currentColor;
}

.bq-mcp-panel .bq-code-option {
    margin-top: 1rem;
}

/* the note under the snippet: the same breath the step copy has above the
   box. Two selectors, because .bq-step p sets every paragraph's margin to 0. */
.bq-mcp-panel .bq-mcp-where {
    margin: 1rem 0 0;
    color: @muted;
    font-size: 0.9rem;
    line-height: 1.6;
}

/* ─── motion ─── */
@media (prefers-reduced-motion: reduce) {
    .bq-art-flow,
    .bq-art-led,
    .bq-art-term--bob,
    .bq-art-logo,
    .bq-art-cursor,
    .bq-map-ring,
    .bq-map-track,
    .ill-flow {
        animation: none;
    }
}

/* ─── responsive ─── */
@media (max-width: 1000px) {
    .bq-hero-grid {
        grid-template-columns: minmax(0, 1fr);
        gap: 3rem;
    }

    /* One column: the art has the whole row, and is sized for 47 columns
       because a motion can push it a couple of columns wide. Centered in that
       row: the component pads every row to one width, so centering each line
       keeps the drawing whole and it does not shift while a motion runs. */
    .bq-hero .bq-ascii {
        font-size: min(1rem, calc((100vw - 3rem) / 31));
    }

    /* stacked, the drawing is as wide as it needs and no wider */
    .bq-hero-art {
        width: 100%;
        max-width: 640px;
        margin: 0 auto;
    }

    .bq-section-header {
        grid-template-columns: minmax(0, 1fr);
        align-items: start;
    }

    .bq-feature-grid {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    /* no room beside the steps: they take the row */
    .bq-how-grid {
        grid-template-columns: minmax(0, 1fr);
    }

    .bq-how-art {
        display: none;
    }

    /* the tab strip of the MCP section is too wide for a tablet or a half screen:
       from 1000px down the dropdown picks the tool instead */
    .bq-tabs {
        display: none;
    }

    .bq-tab-select {
        display: block;
    }
}

@media (max-width: 900px) {
    .bq-wrap {
        padding: 0 1.5rem;
    }

    .bq-hero {
        padding: 2.25rem 0 3rem;
    }

    .bq-section {
        padding: 3.5rem 0;
    }

    .bq-section-header {
        margin-bottom: 2.25rem;
    }

    .bq-why-grid,
    .bq-card-grid {
        grid-template-columns: minmax(0, 1fr);
    }
}

@media (max-width: 640px) {
    /* The drawing's labels are too small to read here, so they go. */
    .bq-art-label {
        display: none;
    }

    /* the map follows its heading straight away on a phone */
    #regions .bq-section-header {
        margin-bottom: 0;
    }

    /* The map: edge to edge and borderless, the drawing twice the screen wide
       so the pins and their labels keep a readable size, panning endlessly
       behind soft fades at both edges. */
    .bq-map {
        margin: 0 -1.5rem;
        padding: 1rem 0 0.75rem;
        border: 0;
        background: transparent;
        box-shadow: none;
        overflow: hidden;
        -webkit-mask-image: linear-gradient(to right, transparent, #000 7%, #000 93%, transparent);
        mask-image: linear-gradient(to right, transparent, #000 7%, #000 93%, transparent);
    }

    .bq-map-track {
        width: max-content;
        animation: bq-pan 24s linear infinite;
    }

    .bq-map-svg {
        width: 760px;
        min-width: 760px;
    }

    .bq-map-copy--2 {
        display: block;
    }

    /* A download row on two lines: the tool's name, then the file name link
       at the right, so a long file name never squeezes the tool's name. */
    .bq-prompt-row {
        flex-wrap: wrap;
        row-gap: 0.4rem;
    }

    .bq-prompt-tool {
        flex: 1 0 calc(100% - 18px - 0.6rem);
    }

    /* the file name link alone takes the second line, at the right */
    .bq-prompt-get {
        margin-left: auto;
        font-size: 0.9rem;
    }

    /* The terminal's lines would be too small as well, so the window is drawn
       twice as large instead, growing upward from its bottom edge into the
       headroom the viewBox gains on a phone (the binding in the template). */
    .bq-art-term-scale {
        transform-box: fill-box;
        transform-origin: 50% 100%;
        transform: scale(2);
    }

    .bq-hero-tags {
        position: static;
        display: flex;
        flex-wrap: wrap;
        gap: 0.5rem;
        width: auto;
        height: auto;
        margin: 1.5rem 0 0;
        overflow: visible;
        clip: auto;
    }

    .bq-hero-actions .bq-btn,
    .bq-close-panel .bq-btn {
        display: block;
        width: 100%;
        box-sizing: border-box;
        text-align: center;
    }

    .bq-hero-install {
        display: block;
    }

    /* a smaller node and a tighter gap: at phone width the rail was taking a
       third of the line from the step's text */
    /* no room for the rail on a phone: the node and the dotted line between
       the nodes go, and the steps keep only their labels */
    .bq-step {
        gap: 0;

        &:not(:last-child)::before {
            display: none;
        }
    }

    .bq-step-node {
        display: none;
    }

    .bq-close-panel {
        padding: 3rem 1.25rem 3.25rem;
    }
    .bq-hero .bq-ascii {
        text-align: center;
    }
}

@media (max-width: 460px) {
    .bq-feature-grid {
        grid-template-columns: minmax(0, 1fr);
    }
}
</style>
